import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { TextFormField } from '~components/FormFields/TextFormField';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { MaterialRateTypeahead } from '~hooks/useMaterialRates/models';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import { makeMaterialRateLabel } from '~pages/Sales/sharedSales/materialRateLabels';
import { isBlankRateValue } from '~pages/Sales/sharedSales/rateRequirements';
import { ItemNameAndId } from '~types/ItemNameAndId';

type MaterialRateDetailsDTO = {
  materialRate: MaterialRateTypeahead | null;
  materialRateUoM: NameIdSchemaRequired | null;
  materialRateBuyPrice: number | null;
  materialRateSellPrice: number | null;
};

export type MaterialRateDetailsHandler = {
  submit: () => Promise<MaterialRateDetailsDTO>;
};

interface MaterialRateDetailsProps {
  initialCompanyId?: string;
  initialMaterialId?: string;
  initialPickupSiteId?: string;
  omitWrapper?: boolean;
  isVisible?: boolean;
  /** The selected project's order-level default for `materialRateItemized`. */
  projectMaterialRateItemizedDefault?: boolean;
  itemizeMaterialRateDefaultOn?: boolean;
  isMaterialRateRequired?: boolean;
}

const MaterialRateDetails = forwardRef<
  MaterialRateDetailsHandler,
  MaterialRateDetailsProps
>(function MaterialRateDetails(
  {
    initialCompanyId,
    initialMaterialId,
    initialPickupSiteId,
    omitWrapper,
    isVisible = true,
    projectMaterialRateItemizedDefault,
    itemizeMaterialRateDefaultOn = false,
    isMaterialRateRequired = false,
  },
  ref,
) {
  const form = useFormContext();
  const { getCompanyMaterialRatesTypeahead } = useMaterialRates();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const { currencyFormatter } = useCompanyCurrency();

  const materialRate = form.watch('materialRate') as MaterialRateTypeahead | null;
  const companyId = form.watch('company.id') as string | null;
  const accountId = form.watch('account.id') as string | null;
  const projectId = form.watch('project.id') as string | null;
  const materialId = form.watch('material.id') as string | null;
  const pickupSiteId = (form.watch('pickUpWayPoint.site.id') ?? null) as string | null;
  const materialRateSellPrice = form.watch('materialRateSellPrice') as
    | string
    | number
    | null;

  const shouldDisableMaterialRateControls = !companyId;
  const itemizedDefault =
    projectMaterialRateItemizedDefault ?? itemizeMaterialRateDefaultOn;
  // With the setting off, a reset unticks itemize even under a project default.
  const itemizedResetValue = itemizeMaterialRateDefaultOn && itemizedDefault;
  // The rate lookup resolves after its effect's render, so a company setting that
  // loads in between must still reach the reset.
  const itemizedResetValueRef = useRef(itemizedResetValue);
  itemizedResetValueRef.current = itemizedResetValue;
  const areMaterialRateFieldsRequired = isMaterialRateRequired && Boolean(materialId);
  const isMaterialRateUoMRequired =
    areMaterialRateFieldsRequired || !isBlankRateValue(materialRateSellPrice);

  // Every write in this component derives or clears a field the user expects
  // to save, and the order form's update payload only carries fields
  // react-hook-form marked dirty — an unflagged write is shown in the UI and
  // then dropped by the PATCH. Each write is gated on an actual change so an
  // untouched form stays pristine.
  const resetMaterialRateFields = useCallback(() => {
    if (form.getValues('materialRate')) {
      form.setValue('materialRate', null, { shouldDirty: true });
    }

    if (form.getValues('materialRateUoM')) {
      form.setValue('materialRateUoM', null, { shouldDirty: true });
    }

    if (!isBlankRateValue(form.getValues('materialRateBuyPrice'))) {
      form.setValue('materialRateBuyPrice', '', { shouldDirty: true });
    }

    if (!isBlankRateValue(form.getValues('materialRateSellPrice'))) {
      form.setValue('materialRateSellPrice', '', { shouldDirty: true });
    }

    const itemizedResetTarget = itemizedResetValueRef.current;
    if (Boolean(form.getValues('materialRateItemized')) !== itemizedResetTarget) {
      form.setValue('materialRateItemized', itemizedResetTarget, { shouldDirty: true });
    }
  }, []);

  // Re-run validation on materialRateUoM when the sell price changes so that
  // the conditional "required" rule (enforced in the form schema) kicks in or
  // clears without needing another submit attempt.
  useEffect(() => {
    if (form.formState.isSubmitted) {
      form.trigger('materialRateUoM');
    }
  }, [materialRateSellPrice, form.formState.isSubmitted]);

  // Track the previous material ID to detect when material is actively cleared
  const previousMaterialIdRef = useRef<string | null>(materialId);

  // The clear-reset below wipes materialRateItemized, and the project cascade
  // that seeded it only runs once per project fetch — so a re-added material
  // must restore the project default here. Restore only after an actual clear
  // (not on first selection) so a manual uncheck isn't overridden.
  const materialWasClearedRef = useRef(false);

  // Clear material rate related fields only when material is actively cleared
  // (changed from having a value to not having one), not on initial mount
  useEffect(() => {
    const hadMaterialBefore = !isNil(previousMaterialIdRef.current);
    const hasNoMaterialNow = !materialId;
    const materialWasReAdded = materialWasClearedRef.current && Boolean(materialId);

    if (hadMaterialBefore && hasNoMaterialNow) {
      resetMaterialRateFields();
      materialWasClearedRef.current = true;
    } else if (materialWasReAdded) {
      if (form.getValues('materialRateItemized') !== itemizedDefault) {
        form.setValue('materialRateItemized', itemizedDefault, { shouldDirty: true });
      }

      materialWasClearedRef.current = false;
    }

    previousMaterialIdRef.current = materialId;
  }, [materialId, resetMaterialRateFields, itemizedDefault]);

  /**
   * @syntaxbliss Please check https://github.com/treadinc/horizon-fe/pull/2867 for further details.
   * This is to prevent auto-selected material rate from overriding the values set
   * in the form default values. This should take place for only one single time:
   * subsequent requests for fetching material rates due to selected company or material
   * changes should indeed be used to override the default values.
   * This one-time prevention is necessary because chances are that a combination of
   * selected company and material may fire an API request returning a single material
   * rate, and if its values are different from the ones set in the order default values,
   * then those will be overridden.
   */
  const firstMaterialRateAutoSelectionPrevented = useRef(false);
  const shouldDeriveValuesFromAutoSelectedMaterialRate = useMemo(() => {
    if (initialCompanyId && initialMaterialId) {
      const selectedCompanyDidChange = initialCompanyId !== companyId;
      const selectedMaterialIdDidChange = initialMaterialId !== materialId;
      const selectedSiteIdDidChange = (initialPickupSiteId ?? null) !== pickupSiteId;
      const valuesDidChange =
        selectedCompanyDidChange ||
        selectedMaterialIdDidChange ||
        selectedSiteIdDidChange;

      if (valuesDidChange) {
        return true;
      }

      if (!firstMaterialRateAutoSelectionPrevented.current) {
        firstMaterialRateAutoSelectionPrevented.current = true;

        return false;
      }
    }

    return true;
  }, [
    initialCompanyId,
    initialMaterialId,
    initialPickupSiteId,
    companyId,
    materialId,
    pickupSiteId,
    // projectId / accountId must be in deps: they gate whether the condition
    // effect refetches (and thus clears rate fields when no match), so the
    // memo has to re-evaluate when they change — otherwise it stays cached
    // at the first-render `false` and the reset never runs.
    projectId,
    accountId,
    materialRate?.id,
  ]);

  // Guards out-of-order typeahead responses: resolving site A then site B must
  // never let A's slower response overwrite B's rate. Each fetch captures the
  // key it incremented to; a response is applied only if it is still the latest.
  const materialRateRequestKeyRef = useRef(0);

  useEffect(() => {
    // Any context change invalidates an in-flight response — including paths
    // that dispatch nothing (e.g. the material was just cleared), otherwise
    // the late response would repopulate the fields the clear-reset wiped.
    const requestKey = materialRateRequestKeyRef.current + 1;
    materialRateRequestKeyRef.current = requestKey;

    if (companyId && materialId && shouldDeriveValuesFromAutoSelectedMaterialRate) {
      getCompanyMaterialRatesTypeahead({
        companyId,
        materialId,
        customerId: accountId || undefined,
        projectId: projectId || undefined,
        siteId: pickupSiteId || undefined,
      }).then((response) => {
        const responseIsStale = requestKey !== materialRateRequestKeyRef.current;
        if (responseIsStale) return;

        // The backend orders candidates most-specific-first (TRE-16144), so
        // the first row is the resolved rate for the current customer /
        // project / pickup site combination.
        const resolvedMaterialRate = response.data[0] ?? null;
        if (resolvedMaterialRate) {
          form.setValue('materialRate', resolvedMaterialRate, { shouldDirty: true });
        } else {
          resetMaterialRateFields();
        }
      });
    }
  }, [companyId, accountId, projectId, materialId, pickupSiteId]);

  // Seeded with the pre-filled materialRate so the clear-on-divergence effect
  // can treat an already-matching form state as if the derive pass had just run.
  const lastAppliedMaterialRateIdRef = useRef<string | null>(
    (form.getValues('materialRate') as MaterialRateTypeahead | null)?.id ?? null,
  );

  useEffect(() => {
    if (materialRate?.id && shouldDeriveValuesFromAutoSelectedMaterialRate) {
      const unit = materialRate.unitOfMeasure ?? '';
      const currentUoM = form.getValues('materialRateUoM') as NameIdSchemaRequired | null;
      if (currentUoM?.id !== unit) {
        form.setValue('materialRateUoM', ItemNameAndId.parse({ id: unit, name: unit }), {
          shouldDirty: true,
        });
      }

      // A catalog rate's missing price stays blank so it saves as null, not a real 0.
      const formatCatalogPrice = (price: number | null | undefined) =>
        isNil(price) ? '' : price.toFixed(2);

      const buyPrice = formatCatalogPrice(materialRate.buyPrice);
      if (form.getValues('materialRateBuyPrice') !== buyPrice) {
        form.setValue('materialRateBuyPrice', buyPrice, { shouldDirty: true });
      }

      const sellPrice = formatCatalogPrice(materialRate.sellPrice);
      if (form.getValues('materialRateSellPrice') !== sellPrice) {
        form.setValue('materialRateSellPrice', sellPrice, { shouldDirty: true });
      }

      lastAppliedMaterialRateIdRef.current = materialRate.id;
    }
  }, [materialRate]);

  const materialRateUoM = form.watch('materialRateUoM') as NameIdSchemaRequired | null;
  const materialRateBuyPrice = form.watch('materialRateBuyPrice') as
    | string
    | number
    | null;
  // materialRateSellPrice is already watched at the top of the component for
  // the conditional UoM-required rule; reuse that binding.

  // Skip the first effect run so a seeded rate (from the order's stamped
  // material_rate on reopen) survives even if its prices/UoM differ from the
  // order's stamped values — clearing should only happen on user edits.
  // Intentionally omit materialRate from deps: a rate change is always
  // followed by the derive effect syncing uom/buy/sell, so divergence seen
  // in the same render is spurious.
  const divergenceCheckHasMountedRef = useRef(false);

  useEffect(() => {
    if (!divergenceCheckHasMountedRef.current) {
      divergenceCheckHasMountedRef.current = true;
      return;
    }
    if (!materialRate?.id) return;
    if (lastAppliedMaterialRateIdRef.current !== materialRate.id) return;

    const uomMatches = materialRate.unitOfMeasure === materialRateUoM?.id;
    const buyMatches =
      Number(materialRateBuyPrice ?? 0).toFixed(2) ===
      Number(materialRate.buyPrice ?? 0).toFixed(2);
    const sellMatches =
      Number(materialRateSellPrice ?? 0).toFixed(2) ===
      Number(materialRate.sellPrice ?? 0).toFixed(2);

    if (!uomMatches || !buyMatches || !sellMatches) {
      form.setValue('materialRate', null, { shouldDirty: true });
      lastAppliedMaterialRateIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialRateUoM, materialRateBuyPrice, materialRateSellPrice]);

  useImperativeHandle(
    ref,
    () => ({
      submit() {
        return new Promise((resolve) => {
          form.handleSubmit((formData) => {
            const data: MaterialRateDetailsDTO = {
              materialRate: formData.materialRate,
              materialRateUoM: formData.materialRateUoM,
              materialRateBuyPrice: formData.materialRateBuyPrice,
              materialRateSellPrice: formData.materialRateSellPrice,
            };

            return resolve(data);
          })();
        });
      },
    }),
    [],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Wrapper omitWrapper={omitWrapper}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 8 }}>
          <AutocompleteAsyncFormField
            asyncCallback={getCompanyMaterialRatesTypeahead}
            clearable
            control={form.control}
            disabled={shouldDisableMaterialRateControls}
            errors={form.formState.errors}
            extraRequestOptions={{
              companyId,
              customerId: accountId || undefined,
              projectId: projectId || undefined,
              materialId: materialId || undefined,
              siteId: pickupSiteId || undefined,
            }}
            getLabel={(item: MaterialRateTypeahead) =>
              item ? makeMaterialRateLabel(item, currencyFormatter) : ''
            }
            getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
            label={`${t('form_fields.material_rate')}`}
            name="materialRate"
            dataUi={`page-sales-orders__material-rate-details__material-rate-input`}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <AutocompleteFormField
            control={form.control}
            disabled={shouldDisableMaterialRateControls}
            errors={form.formState.errors}
            getLabel={(item: NameIdSchemaRequired | null) => item?.name ?? ''}
            getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
            isRequired={isMaterialRateUoMRequired}
            label={`${t('form_fields.unit')}`}
            list={unitOfMeasureOptions}
            name="materialRateUoM"
            dataUi={`page-sales-orders__material-rate-details__material-rate-uo-m-input`}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <TextFormField
            control={form.control}
            disabled={shouldDisableMaterialRateControls}
            errors={form.formState.errors}
            isRequired={areMaterialRateFieldsRequired}
            label={`${t('administration.material_rates.buy_price')}`}
            name="materialRateBuyPrice"
            type="number"
            InputProps={{
              startAdornment: (
                <Typography
                  sx={{
                    mr: 1,
                    fontSize: 'inherit',
                  }}
                >
                  $
                </Typography>
              ),
            }}
            dataUi={`page-sales-orders__material-rate-details__material-rate-buy-price-input`}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <TextFormField
            control={form.control}
            disabled={shouldDisableMaterialRateControls}
            errors={form.formState.errors}
            isRequired={areMaterialRateFieldsRequired}
            label={`${t('administration.material_rates.sell_price')}`}
            name="materialRateSellPrice"
            type="number"
            InputProps={{
              startAdornment: (
                <Typography
                  sx={{
                    mr: 1,
                    fontSize: 'inherit',
                  }}
                >
                  $
                </Typography>
              ),
            }}
            dataUi={`page-sales-orders__material-rate-details__material-rate-sell-price-input`}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <FormFieldLabel label={t('administration.material_rates.invoice_setting')} />
          <CheckBoxFormField
            control={form.control}
            errors={form.formState.errors}
            label={`${t('form_fields.itemize_material_rate')}`}
            name="materialRateItemized"
            value={Boolean(form.getValues('materialRateItemized'))}
            sx={{
              [`& .${formControlLabelClasses.root}`]: { mr: 0 },
              [`& .${checkboxClasses.root}`]: { pr: '2px' },
              [`& .${typographyClasses.root}`]: { fontSize: '12px' },
            }}
            dataUi={`page-sales-orders__material-rate-details__material-rate-itemized-input`}
          />
        </Grid>
      </Grid>
    </Wrapper>
  );
});

const Wrapper = memo(function Memo({
  children,
  omitWrapper,
}: React.PropsWithChildren<{ omitWrapper?: boolean }>) {
  const theme = useTheme();

  if (omitWrapper) {
    return children;
  }

  return (
    <Box
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {children}
    </Box>
  );
});

export default MaterialRateDetails;
